import { VIDEO_TEMPLATE_ROUTE, WAVE_EVENT_ROUTE } from '@anm/constants/routes';
import {
  AspectRatio,
  AspectRatioLabel,
  ASPECT_MAP,
  RATIO_ASPECT_LABEL_MAP
} from '@anm/data/aspects';
import compactProps from '@anm/helpers/compactProps';
import usePageParams from '@anm/hooks/usePageParams';
import useResize from '@anm/hooks/useResize';
import omitBy from 'lodash/fp/omitBy';
import { ParsedUrlQuery } from 'querystring';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import routes from '../../../routes';
import { filterSelectors } from '../../../store/modules/filter';
import { templateSelectors } from '../../../store/modules/template';
import {
  selectMediumSize,
  selectSmallSize
} from '../../../store/modules/template/selectors';
import { selectVideoFormatByName } from '../../../store/modules/video';
import { VideoRatioItemProps } from '../VideoRatioList';

import { VideoData } from '.';

const { Router } = routes;

type GetInitDataParams = {
  currentRatioItem?: VideoRatioItemProps;
};

const useMediaTarget = () => {
  const { aspects: aspectsFromParams } = usePageParams();
  const savedFilters = useSelector(filterSelectors.selectSavedFilters);
  const template = useSelector(templateSelectors.selectCurrentTemplate);

  if (!template) return '';

  const { defaultTarget } = template.meta;

  const mediaKeys = Object.keys(template.meta.media);
  const aspectStr =
    ASPECT_MAP[aspectsFromParams as AspectRatioLabel] || savedFilters?.aspects;
  const aspects = aspectStr?.split(',') || [];

  const mediaTarget = aspects?.reduce(
    (acc, aspect) => (mediaKeys.includes(aspect) ? aspect : acc),
    defaultTarget
  ) as string;

  return mediaTarget;
};

const useVideoData = ({ currentRatioItem }: GetInitDataParams): VideoData => {
  const mediaTarget = useMediaTarget();
  const template = useSelector(templateSelectors.selectCurrentTemplate);

  if (!template) return {} as VideoData;

  const { defaultTarget, media } = template.meta;
  const templateMedia = media[mediaTarget];

  const video = currentRatioItem?.video || templateMedia.video;
  const videoUrl = selectSmallSize(video).url;

  const thumb = currentRatioItem?.thumb || templateMedia.thumb;
  const poster = selectMediumSize(thumb).url;

  return {
    poster,
    videoUrl,
    activeRatioName: mediaTarget || defaultTarget
  };
};

export const useRefWithUpdateOnResize = <T extends any>() => {
  const ref = useRef<T>(null);
  const [, update] = useState({});

  useResize(() => update({}));

  return ref;
};

export const useTemplateVideoPlayer = (
  currentRatioItem?: VideoRatioItemProps
) => {
  const initialVideoData = useVideoData({
    currentRatioItem
  });
  const mediaTarget = useMediaTarget();

  useEffect(() => {
    setVideoData(initialVideoData);
  }, [mediaTarget]);

  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isPlayEventSent, setIsPlayEventSent] = useState(false);
  const [videoData, setVideoData] = useState<VideoData>(initialVideoData);

  return {
    setIsPlayEventSent,
    setIsVideoLoaded,
    isPlayEventSent,
    isVideoLoaded,
    setVideoData,
    videoData
  };
};

export const useChangeFormatUrl = (currentRatioItem?: VideoRatioItemProps) => {
  const mediaTarget = useMediaTarget();
  const template = useSelector(templateSelectors.selectCurrentTemplate);

  const defaultVideoFormat = useSelector(state =>
    selectVideoFormatByName(state, mediaTarget)
  );

  const ratio = (currentRatioItem?.format ||
    defaultVideoFormat?.format) as AspectRatio;

  const isFirstRender = useRef(true);
  const canWatchQuery = useRef(false);

  const getParams = (parsedQuery?: ParsedUrlQuery) => {
    const routerProps = omitBy((_, k) => k === 'page')(parsedQuery);
    const defaultAspect = RATIO_ASPECT_LABEL_MAP[ratio];
    const aspects = isFirstRender.current
      ? routerProps.aspects || defaultAspect
      : defaultAspect;

    const params = compactProps({
      ...routerProps,
      aspects,
      pageName: template?.slug
    });

    return params;
  };

  const getIsWaveEventPage = (route?: string) => route === WAVE_EVENT_ROUTE;

  useEffect(() => {
    if (getIsWaveEventPage(Router.router?.route)) return;

    const params = getParams(Router.router?.query);

    Router.pushRoute(VIDEO_TEMPLATE_ROUTE, params, { shallow: true });

    isFirstRender.current = false;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) return;
    if (!canWatchQuery.current) {
      canWatchQuery.current = true;
      return;
    }

    const params = getParams(Router.router?.query);

    Router.pushRoute(VIDEO_TEMPLATE_ROUTE, params, { shallow: true });
  }, [template?.slug, Router.router?.query, RATIO_ASPECT_LABEL_MAP[ratio]]);
};
